import { useMutation } from "@apollo/client";
import { Button, Checkbox, Divider, Form, Input, Radio, Select, Space, message } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { isFunction } from "lodash";
import { UserContext } from "../../amplify/authenticator-provider";
import { SAVE_ENAGEMENT_MESSAGE_TEMPLATE } from "../../graphql/mutation";
import { closeDrawer } from "../../library/helpers";
import { setDrawerLoading } from "../../library/drawerLoadingService";
import { RichTextEditorJSX } from "../userSettings/RichTextEditor";
import { isPlatformAdmin } from "../../hooks/utils";
import useLang from "../../hooks/useLang";
import useLocation from "../../hooks/useLocation";
import useLocationHelpers from "../../hooks/useLocationHelpers";
import TemplateDepartmentScopes from "./TemplateDepartmentScopes";
import {
  ALL_DEPARTMENTS_VALUE,
  TEMPLATE_CHANNELS_ENABLED,
  TEMPLATE_CHANNEL_OPTIONS,
  TEMPLATE_CUSTOM_TYPES_ENABLED,
  TEMPLATE_DEFAULT_CHANNELS,
  TEMPLATE_LEVELS,
  TEMPLATE_LEVEL_OPTIONS,
  TEMPLATE_TYPES,
  TEMPLATE_TYPE_OPTIONS,
} from "../../constant/TemplateConstant";
import {
  buildTemplateSaveVariables,
  deriveTemplateLevel,
  findTemplateTypeByName,
  getTemplateChannels,
  getTemplateLocationIds,
  getTemplateScopes,
  isTemplateMessageEmpty,
  normaliseDepartmentScopes,
  templateNeedsSubject,
  toTemplateId,
} from "../../helpers/templateHelpers";

const EMPTY_SCOPE = { locationId: null, departmentIds: [], allDepartments: false };

/**
 * The panel the Location and Department scopes sit in — a tinted, bordered
 * block so the scope choices read as belonging to the radio above them rather
 * than as more top-level fields.
 */
const ScopePanel = ({ title, children }) => (
  <div className="flex flex-col gap-3 p-4 rounded-lg border border-solid border-secondary bg-fill-quaternary">
    <span className="text-[13px] text-default">{title}</span>
    {children}
  </div>
);

/**
 * The Type select's "+ Add type" footer.
 *
 * Collapsed it is a link; opened it is a name field and an Add button. A name
 * that already exists — in any casing — selects the existing type instead of
 * creating a second one that only differs by case.
 */
const AddTemplateTypeFooter = ({ options, onAdd }) => {
  const [lang] = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");

  const handleAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const existing = findTemplateTypeByName(options, trimmed);
    if (existing) {
      message.info(lang.typeAlreadyExists);
      onAdd(existing.value);
    } else {
      onAdd(trimmed);
    }
    setName("");
    setIsOpen(false);
  };

  return (
    <>
      <Divider className="my-1" />
      <div className="px-2 pb-1" onMouseDown={event => event.preventDefault()}>
        {isOpen ? (
          <Space.Compact className="w-full">
            <Input
              autoFocus
              value={name}
              placeholder={lang.newTypeName}
              onChange={event => setName(event.target.value)}
              onPressEnter={handleAdd}
            />
            <Button onClick={handleAdd}>{lang.add}</Button>
          </Space.Compact>
        ) : (
          <Button type="link" className="px-0" onClick={() => setIsOpen(true)}>
            {lang.addTypeAction}
          </Button>
        )}
      </div>
    </>
  );
};

const TemplateForm = ({
  template,
  handleRefetch,
  companyId,
  mode = "ADD",
  // The drawer footer lives outside this component, so the caller owns the form
  // instance and submits it. Falls back to a local instance when rendered
  // standalone.
  form: formFromDrawer,
  // Surfaces that only ever create one kind of template (the email drawer)
  // pass it here: the type is forced and its selector hidden.
  lockedTemplateType = null,
}) => {
  const [lang] = useLang();
  const [localForm] = Form.useForm();
  const form = formFromDrawer || localForm;
  const { user, authData } = useContext(UserContext);
  const { userLocationOptions } = useLocationHelpers();
  const isPlatformAdminUser = isPlatformAdmin(authData);
  const isEditMode = mode === "EDIT";

  // A user-created type is not in the enum, so the select carries it as an
  // extra option for as long as the drawer is open.
  const [customTypes, setCustomTypes] = useState([]);

  const initialValues = useMemo(
    () => ({
      title: "",
      subject: "",
      templateType: lockedTemplateType || TEMPLATE_TYPES.ENGAGEMENT,
      channels: TEMPLATE_DEFAULT_CHANNELS,
      // A new template starts private: the author opts into sharing it, rather
      // than publishing company-wide by default.
      level: TEMPLATE_LEVELS.USER,
      // LOCATION narrows by locationIds; DEPARTMENT uses one scope row per
      // location. USER is every location the author can reach.
      locationIds: [],
      scopes: [EMPTY_SCOPE],
    }),
    [lockedTemplateType]
  );

  const [formData, setformData] = useState({ message: "" });

  useEffect(() => {
    if (!isEditMode || !template) {
      setformData({ message: "" });
      form.setFieldsValue(initialValues);
      return;
    }
    // "All departments" round-trips through the picker's sentinel option, so an
    // edited template comes back with the same row it was saved with.
    const savedScopes = getTemplateScopes(template).map(scope => ({
      locationId: String(scope.locationId),
      departmentIds: scope.allDepartments
        ? [ALL_DEPARTMENTS_VALUE]
        : scope.departmentIds.map(String),
      allDepartments: scope.allDepartments,
    }));
    // A saved template may carry a type this build does not know — keep it
    // selectable so editing anything else does not silently retype it.
    const savedType = lockedTemplateType || template.templateType || TEMPLATE_TYPES.ENGAGEMENT;
    if (savedType && !TEMPLATE_TYPE_OPTIONS.some(option => option.value === savedType)) {
      setCustomTypes(prev => (prev.includes(savedType) ? prev : [...prev, savedType]));
    }
    setformData({ message: template.message || "" });
    form.setFieldsValue({
      ...initialValues,
      title: template.title || "",
      subject: template.subject || "",
      templateType: savedType,
      channels: getTemplateChannels(template),
      level: deriveTemplateLevel(template),
      locationIds: getTemplateLocationIds(template).map(String),
      scopes: savedScopes.length ? savedScopes : [EMPTY_SCOPE],
    });
  }, [template, isEditMode]);

  const level = Form.useWatch("level", form) ?? initialValues.level;
  const templateType = Form.useWatch("templateType", form) ?? initialValues.templateType;

  const needsCompanyLocations =
    isPlatformAdminUser &&
    (level === TEMPLATE_LEVELS.DEPARTMENT || level === TEMPLATE_LEVELS.LOCATION);

  // Locations are picked per company; a platform admin can reach every location
  // in the company, everyone else only the ones assigned to them.
  const { data: companyLocations } = useLocation({
    variables: { companyId: toTemplateId(companyId) },
    skip: !needsCompanyLocations || !toTemplateId(companyId),
  });

  // Alphabetical, in both the Location checkbox list and the Department rows'
  // location select: neither source orders by name, and a dealer group's
  // locations arrive in id order, which reads as no order at all.
  const scopeLocationOptions = useMemo(() => {
    const options = isPlatformAdminUser
      ? (companyLocations?.getLocations || []).map(location => ({
          value: String(location.id),
          label: location.title || location.name || `Location ${location.id}`,
        }))
      : userLocationOptions || [];
    return [...options].sort((a, b) =>
      String(a?.label ?? "").localeCompare(String(b?.label ?? ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [isPlatformAdminUser, userLocationOptions, companyLocations]);

  const typeOptions = useMemo(
    () => [...TEMPLATE_TYPE_OPTIONS, ...customTypes.map(value => ({ value, label: value }))],
    [customTypes]
  );

  const handleAddType = value => {
    setCustomTypes(prev => (prev.includes(value) ? prev : [...prev, value]));
    form.setFieldsValue({ templateType: value });
  };

  const [saveEngagementMessageTemplates] = useMutation(SAVE_ENAGEMENT_MESSAGE_TEMPLATE);

  const handleSave = async values => {
    if (isTemplateMessageEmpty(formData.message)) {
      return message.error("Please enter the message");
    }

    // A locked type has no rendered field, and antd only returns registered
    // fields to onFinish — so it is resolved here rather than read off values.
    const resolvedTemplateType = lockedTemplateType || values.templateType;

    if (TEMPLATE_CHANNELS_ENABLED && !(values.channels || []).length) {
      return message.error(lang.channelRequired);
    }
    if (values.level === TEMPLATE_LEVELS.LOCATION && !(values.locationIds || []).length) {
      return message.error("Please select at least one location");
    }
    if (
      values.level === TEMPLATE_LEVELS.DEPARTMENT &&
      !normaliseDepartmentScopes(values.scopes).length
    ) {
      return message.error("Please select a location and its departments");
    }

    try {
      setDrawerLoading(true);
      const res = await saveEngagementMessageTemplates({
        variables: buildTemplateSaveVariables({
          id: template?.id,
          userId: user?.id,
          companyId,
          title: values.title,
          message: formData.message,
          subject: values.subject,
          templateType: resolvedTemplateType,
          isActive: isEditMode ? template?.isActive : true,
          level: values.level,
          // USER level never narrows by location: the LOCATION query scope
          // filters on location_id alone, so a location here would expose a
          // private template to everyone at that location.
          locationIds: values.level === TEMPLATE_LEVELS.LOCATION ? values.locationIds : [],
          scopes: values.scopes,
        }),
      });
      if (res.data.saveEngagementMessageTemplate.statusCode === 200) {
        if (isFunction(handleRefetch)) await handleRefetch();
        message.success(res.data.saveEngagementMessageTemplate.message);
        closeDrawer();
      } else {
        message.error(res.data.saveEngagementMessageTemplate.message);
      }
    } catch (err) {
      message.error(err?.message || "Failed to save the template. Please try again.");
    } finally {
      setDrawerLoading(false);
    }
  };

  return (
    <Form form={form} onFinish={handleSave} layout="vertical" initialValues={initialValues}>
      <div className="flex flex-col w-full">
        <Form.Item
          label={lang.name}
          name="title"
          rules={[{ required: true, message: lang.templateNameRequired }]}
        >
          <Input name="title" placeholder={lang.templateNamePlaceholder} />
        </Form.Item>

        {!lockedTemplateType && (
          <Form.Item
            label={lang.type}
            name="templateType"
            rules={[{ required: true, message: lang.templateTypeRequired }]}
          >
            <Select
              options={typeOptions}
              placeholder={lang.templateTypeRequired}
              // Creating a type is deploy-gated: `templateType` is a server
              // enum, so a new one is rejected at validation time.
              dropdownRender={menu => (
                <>
                  {menu}
                  {TEMPLATE_CUSTOM_TYPES_ENABLED && (
                    <AddTemplateTypeFooter options={typeOptions} onAdd={handleAddType} />
                  )}
                </>
              )}
            />
          </Form.Item>
        )}

        {/* Channels are deploy-gated: the save mutation has no channel argument,
            so a pick here would be dropped on save. */}
        {TEMPLATE_CHANNELS_ENABLED && (
          <Form.Item
            label={lang.channel}
            name="channels"
            rules={[{ required: true, message: lang.channelRequired }]}
          >
            <Checkbox.Group options={TEMPLATE_CHANNEL_OPTIONS} />
          </Form.Item>
        )}

        {templateNeedsSubject(templateType) && (
          <Form.Item label="Subject" name="subject">
            <Input name="subject" placeholder="Email Subject" />
          </Form.Item>
        )}

        <span className="text-[14px] text-default mb-2">
          <span className="text-error mr-1">*</span>
          {lang.message}
        </span>
        <RichTextEditorJSX
          formData={formData}
          setformData={setformData}
          changingKey="message"
          className="min-h-[200px] h-[200px] w-full p-2"
          showPreview={false}
          showSnippetHeader={false}
          snippetSearchPlaceholder={lang.searchPlaceholders}
        />

        <div className="flex flex-col mt-4 gap-3">
          <Form.Item
            className="mb-0"
            label={lang.shareWith}
            name="level"
            rules={[{ required: true, message: "Please choose who this template is shared with" }]}
          >
            <Radio.Group
              options={TEMPLATE_LEVEL_OPTIONS}
              onChange={() =>
                form.setFieldsValue({
                  // Changing level clears what the previous one owned. Nothing
                  // is pre-selected: the session's active location is not
                  // necessarily one the template API will accept, and seeding it
                  // made saves fail on a location the author never ticked.
                  locationIds: [],
                  scopes: [EMPTY_SCOPE],
                })
              }
            />
          </Form.Item>

          {level === TEMPLATE_LEVELS.LOCATION && (
            <ScopePanel title={lang.selectLocations}>
              <Form.Item
                className="mb-0"
                name="locationIds"
                rules={[{ required: true, message: "Select at least one location" }]}
              >
                {/* The rows live in a plain flex column inside the group —
                    `.ant-checkbox-group` lays its wrappers out inline, which on
                    a company with dozens of locations spills into columns that
                    clip the longer names.

                    The group is `inline-flex`, so this container is a flex item
                    and would otherwise size to its widest label, leaving the
                    scrollbar floating mid-panel instead of on the edge —
                    `w-full` holds it to the panel width. */}
                <Checkbox.Group className="w-full">
                  <div className="w-full flex flex-col gap-2 max-h-[220px] overflow-y-auto pr-1">
                    {scopeLocationOptions.map(option => (
                      <Checkbox key={option.value} value={option.value} className="!ml-0">
                        <span className="break-words">{option.label}</span>
                      </Checkbox>
                    ))}
                  </div>
                </Checkbox.Group>
              </Form.Item>
            </ScopePanel>
          )}

          {level === TEMPLATE_LEVELS.DEPARTMENT && (
            <ScopePanel title={lang.locationsAndDepartments}>
              <TemplateDepartmentScopes form={form} locationOptions={scopeLocationOptions} />
            </ScopePanel>
          )}
        </div>
      </div>
    </Form>
  );
};

export default TemplateForm;
